import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import {
  getPanchayatDataProvider,
  type PanchayatBlockRecord,
  type PanchayatDistrictRecord,
  type PanchayatGramPanchayatRecord,
} from '@/lib/panchayat-data'

const INSTITUTION_SESSION_KEY = "institution_selection_v1"
const PERF_DEBUG_SESSION_ID = "slow-page-load"

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!perfDebugUrl) return
  fetch(perfDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PERF_DEBUG_SESSION_ID,
      runId: "pre-fix",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

type PersistedSelectionV2 = {
  districtId?: string
  districtNameEn?: string
  blockId?: string
  blockNameEn?: string
  villageId?: string
  villageNameEn?: string
}

function normalizeName(value: string) {
  return String(value || "").trim().toLowerCase()
}

function readInstitutionFromSession(
  districtRecords: Array<{ id: string; names: { en?: string; hi?: string; pa?: string } }>,
  blockRecords: Array<{ id: string; names: { en?: string; hi?: string; pa?: string }; districtId?: string }>,
): { district: string; block: string; village: string } {
  try {
    if (typeof window === "undefined") return { district: "", block: "", village: "" }
    const raw = sessionStorage.getItem(INSTITUTION_SESSION_KEY)
    if (!raw) return { district: "", block: "", village: "" }
    const parsed = JSON.parse(raw) as PersistedSelectionV2 & { district?: unknown; block?: unknown; village?: unknown }

    const village = typeof parsed.village === "string" ? parsed.village : typeof parsed.villageId === "string" ? parsed.villageId : ""

    // District: strict ID first; else legacy name resolve to single ID from districtRecords
    let district = ""
    if (typeof parsed.districtId === "string" && parsed.districtId) {
      district = parsed.districtId
    } else if (typeof parsed.district === "string" && parsed.district) {
      const strict = districtRecords.find((d) => normalizeName(d.id) === normalizeName(parsed.district as string))
      if (strict) district = strict.id
      else {
        const candidates = districtRecords.filter((d) =>
          normalizeName(d.id) === normalizeName(parsed.district as string) ||
          normalizeName(d.names?.en || "") === normalizeName(parsed.district as string) ||
          normalizeName(d.names?.hi || "") === normalizeName(parsed.district as string) ||
          normalizeName(d.names?.pa || "") === normalizeName(parsed.district as string),
        )
        if (candidates.length === 1) district = candidates[0].id
        else district = parsed.district as string
      }
    }

    // Block: strict ID first; else legacy name → candidates filtered by district.id match 1
    let block = ""
    const districtForBlock = districtRecords.find((d) => normalizeName(d.id) === normalizeName(district)) || null
    if (typeof parsed.blockId === "string" && parsed.blockId) {
      block = parsed.blockId
    } else if (typeof parsed.block === "string" && parsed.block) {
      const strict = blockRecords.find((b) => normalizeName(b.id) === normalizeName(parsed.block as string))
      if (strict) {
        if (!districtForBlock?.id || !strict.districtId || normalizeName(strict.districtId) === normalizeName(districtForBlock.id)) {
          block = strict.id
        }
      }
      if (!block) {
        const candidates = blockRecords.filter((b) => {
          const idMatch = normalizeName(b.id) === normalizeName(parsed.block as string)
          const nameMatch =
            normalizeName(b.names?.en || "") === normalizeName(parsed.block as string) ||
            normalizeName(b.names?.hi || "") === normalizeName(parsed.block as string) ||
            normalizeName(b.names?.pa || "") === normalizeName(parsed.block as string)
          if (!idMatch && !nameMatch) return false
          if (districtForBlock?.id && b.districtId && normalizeName(b.districtId) !== normalizeName(districtForBlock.id)) return false
          return true
        })
        if (candidates.length === 1) block = candidates[0].id
        else block = parsed.block as string
      }
    }

    return { district, block, village }
  } catch {
    return { district: "", block: "", village: "" }
  }
}

function writeInstitutionToSession(
  next: { district: string; block: string; village: string },
  districtRecords: Array<{ id: string; names: { en?: string; hi?: string; pa?: string } }>,
  blockRecords: Array<{ id: string; names: { en?: string; hi?: string; pa?: string }; districtId?: string }>,
) {
  try {
    if (typeof window === "undefined") return
    const dRec = districtRecords.find((d) => normalizeName(d.id) === normalizeName(next.district)) || null
    const bRec = blockRecords.find((b) => normalizeName(b.id) === normalizeName(next.block)) || null
    const normalized: PersistedSelectionV2 = {
      districtId: String(next.district || ""),
      districtNameEn: dRec?.names?.en || undefined,
      blockId: String(next.block || ""),
      blockNameEn: bRec?.names?.en || undefined,
      villageId: String(next.village || ""),
    }
    sessionStorage.setItem(INSTITUTION_SESSION_KEY, JSON.stringify(normalized))
  } catch {
    void 0
  }
}

interface LocationContextType {
  // Selection State
  selectedDistrict: string
  selectedBlock: string
  selectedVillage: string

  // Setters
  setSelectedDistrict: (district: string) => void
  setSelectedBlock: (block: string) => void
  setSelectedVillage: (village: string) => void

  // Data
  loading: boolean
  error: Error | null
  retry: () => void

  // Derived Lists
  districts: { id: string; names: { en: string; hi: string; pa: string } }[]
  blocks: { id: string; names: { en: string; hi: string; pa: string } }[]
  villages: { id: string; names: { en: string; hi: string; pa: string } }[]
}

const LocationContext = createContext<LocationContextType | undefined>(undefined)

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const [districtRecords, setDistrictRecords] = useState<PanchayatDistrictRecord[]>([])
  const [blockRecords, setBlockRecords] = useState<PanchayatBlockRecord[]>([])
  const [villageRecords, setVillageRecords] = useState<PanchayatGramPanchayatRecord[]>([])
  const initialInstitution = useMemo(() => readInstitutionFromSession(districtRecords, blockRecords), [districtRecords, blockRecords])
  const [selectedDistrict, setSelectedDistrict] = useState(initialInstitution.district)
  const [selectedBlock, setSelectedBlock] = useState(initialInstitution.block)
  const [selectedVillage, setSelectedVillage] = useState(initialInstitution.village)
  const provider = useMemo(() => getPanchayatDataProvider(), [])
  const [loadingDistricts, setLoadingDistricts] = useState(false)
  const [loadingBlocks, setLoadingBlocks] = useState(false)
  const [loadingVillages, setLoadingVillages] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const loadDistricts = useCallback(async () => {
    setLoadingDistricts(true)
    setError(null)
    try {
      const hierarchyStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()
      const next = await provider.getDistricts()
      setDistrictRecords(next)
      reportPerfDebug("C", "src/contexts/LocationContext.tsx", "[DEBUG] District list loaded", {
        districtCount: next.length,
        elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - hierarchyStartMs,
      })
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)))
    } finally {
      setLoadingDistricts(false)
    }
  }, [provider])

  const loadBlocks = useCallback(async (districtId: string) => {
    const normalizedDistrictId = String(districtId || "").trim()
    if (!normalizedDistrictId) {
      setBlockRecords([])
      setVillageRecords([])
      return
    }
    setLoadingBlocks(true)
    setError(null)
    try {
      const next = await provider.getBlocks(normalizedDistrictId)
      setBlockRecords(next)
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)))
    } finally {
      setLoadingBlocks(false)
    }
  }, [provider])

  const loadVillages = useCallback(async (blockId: string, districtId: string) => {
    const normalizedBlockId = String(blockId || "").trim()
    const normalizedDistrictId = String(districtId || "").trim()
    if (!normalizedBlockId || !normalizedDistrictId) {
      setVillageRecords([])
      return
    }
    setLoadingVillages(true)
    setError(null)
    try {
      const next = await provider.getGramPanchayats(normalizedBlockId, normalizedDistrictId)
      setVillageRecords(next)
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError : new Error(String(nextError)))
    } finally {
      setLoadingVillages(false)
    }
  }, [provider])

  useEffect(() => {
    void loadDistricts()
  }, [loadDistricts])

  useEffect(() => {
    if (!selectedDistrict) {
      setBlockRecords([])
      setVillageRecords([])
      return
    }
    void loadBlocks(selectedDistrict)
  }, [loadBlocks, selectedDistrict])

  useEffect(() => {
    if (!selectedDistrict || !selectedBlock) {
      setVillageRecords([])
      return
    }
    void loadVillages(selectedBlock, selectedDistrict)
  }, [loadVillages, selectedBlock, selectedDistrict])

  useEffect(() => {
    if (!selectedDistrict || !selectedBlock || !selectedVillage) return
    let cancelled = false
    void provider
      .getInstitution(selectedVillage, selectedDistrict, selectedBlock)
      .then(() => {
        if (cancelled) return
      })
      .catch(() => {
        if (cancelled) return
      })
    return () => {
      cancelled = true
    }
  }, [provider, selectedBlock, selectedDistrict, selectedVillage])

  const retry = useCallback(() => {
    if (!selectedDistrict) {
      void loadDistricts()
      return
    }
    if (!selectedBlock) {
      void loadBlocks(selectedDistrict)
      return
    }
    void loadVillages(selectedBlock, selectedDistrict)
  }, [loadBlocks, loadDistricts, loadVillages, selectedBlock, selectedDistrict])

  const loading = loadingDistricts || loadingBlocks || loadingVillages

  const districts = useMemo(() => {
    return districtRecords.map((district) => ({
      id: district.id,
      names: district.names,
    }))
  }, [districtRecords])

  const blocks = useMemo(() => {
    return blockRecords.map((block) => ({
      id: block.id,
      names: block.names,
    }))
  }, [blockRecords])

  const villages = useMemo(() => {
    return villageRecords.map((village) => ({
      id: village.id,
      names: village.names,
    }))
  }, [villageRecords])

  // Reset dependent selections
  const handleSetDistrict = useCallback((district: string) => {
    setSelectedDistrict(district)
    setSelectedBlock('')
    setSelectedVillage('')
  }, [])

  const handleSetBlock = useCallback((block: string) => {
    setSelectedBlock(block)
    setSelectedVillage('')
  }, [])

  useEffect(() => {
    if (districtRecords.length === 0) return
    // Re-normalize legacy-name selections to IDs after districts/blocks load.
    const norm = readInstitutionFromSession(districtRecords, blockRecords)
    const districtFromSession = norm.district
    const blockFromSession = norm.block
    const villageFromSession = norm.village

    // District: normalize ID only if current selection resolves to name only and matches single record.
    setSelectedDistrict((prev) => {
      if (!prev) return prev
      const isIdAlready = districtRecords.some((d) => normalizeName(d.id) === normalizeName(prev))
      if (isIdAlready) return prev
      if (districtFromSession && districtRecords.some((d) => normalizeName(d.id) === normalizeName(districtFromSession))) {
        return districtFromSession
      }
      const strictName = districtRecords.filter((d) =>
        normalizeName(d.names?.en || "") === normalizeName(prev) ||
        normalizeName(d.names?.hi || "") === normalizeName(prev) ||
        normalizeName(d.names?.pa || "") === normalizeName(prev),
      )
      if (strictName.length === 1) return strictName[0].id
      return prev
    })

    setSelectedBlock((prev) => {
      if (!prev) return prev
      const isIdAlready = blockRecords.some((b) => normalizeName(b.id) === normalizeName(prev))
      if (isIdAlready) return prev
      if (blockFromSession && blockRecords.some((b) => normalizeName(b.id) === normalizeName(blockFromSession))) {
        return blockFromSession
      }
      return prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtRecords.length, blockRecords.length])

  useEffect(() => {
    writeInstitutionToSession({ district: selectedDistrict, block: selectedBlock, village: selectedVillage }, districtRecords, blockRecords)
  }, [selectedBlock, selectedDistrict, selectedVillage, districtRecords, blockRecords])

  const value = useMemo(() => ({
    selectedDistrict,
    selectedBlock,
    selectedVillage,
    setSelectedDistrict: handleSetDistrict,
    setSelectedBlock: handleSetBlock,
    setSelectedVillage,
    loading,
    error,
    retry,
    districts,
    blocks,
    villages
  }), [
    selectedDistrict, selectedBlock, selectedVillage,
    handleSetDistrict, handleSetBlock, setSelectedVillage,
    loading, error, retry,
    districts, blocks, villages
  ])

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  )
}

export function useLocationState() {
  const context = useContext(LocationContext)
  if (context === undefined) {
    throw new Error('useLocationState must be used within a LocationProvider')
  }
  return context
}
